/**
 * Mock External Access API Client
 *
 * Returns fixtures instead of making HTTP requests, so the portal runs
 * standalone (Vite dev with VITE_USE_MOCKS, and Playwright E2E) with the
 * External access section behaving like the real thing.
 *
 * The default fixture is EMPTY. That is the interesting default: D6 says the
 * table hides at zero rows, so an empty mock exercises the state most
 * organizations are actually in, and a test that wants rows says so.
 *
 * @module api/ExternalAccessApiClient.mock
 */

import type {
  ExternalAccessGrant,
  ExternalAccessResponse,
  ExternalAccessMutationResponse,
  UserRole,
} from '../types';
import type { ExternalAccessApiClient } from './ExternalAccessApiClient';
import { HttpError } from './http';

/**
 * A single outside account, for the case where a test or a dev session wants
 * the table populated.
 */
export const DEFAULT_EXTERNAL_ACCESS_FIXTURE: ExternalAccessGrant[] = [];

export type MockExternalAccessErrorConfig =
  | { type: 'none' }
  | { type: '401' }
  | { type: '403' }
  | { type: 'custom'; status: number; message: string };

export interface MockExternalAccessApiClientConfig {
  /** Grants returned from listExternalAccess(). @default [] */
  items?: ExternalAccessGrant[];
  /** Whether the backend reports its scan as cut short. @default false */
  truncated?: boolean;
  /** Whether the caller may grant and revoke. @default true */
  canManage?: boolean;
  /** Error configuration for listExternalAccess(). @default { type: 'none' } */
  listError?: MockExternalAccessErrorConfig;
  /** Error configuration for the mutations. @default { type: 'none' } */
  mutationError?: MockExternalAccessErrorConfig;
}

function toError(config: MockExternalAccessErrorConfig): HttpError | null {
  switch (config.type) {
    case 'none':
      return null;
    case '401':
      return new HttpError(401, 'Unauthorized');
    case '403':
      return new HttpError(403, 'Forbidden');
    case 'custom':
      return new HttpError(config.status, config.message);
  }
}

export class MockExternalAccessApiClient implements ExternalAccessApiClient {
  private items: ExternalAccessGrant[];
  private truncated: boolean;
  private canManage: boolean;
  private listError: MockExternalAccessErrorConfig;
  private mutationError: MockExternalAccessErrorConfig;

  constructor(config: MockExternalAccessApiClientConfig = {}) {
    this.items = config.items ?? [...DEFAULT_EXTERNAL_ACCESS_FIXTURE];
    this.truncated = config.truncated ?? false;
    this.canManage = config.canManage ?? true;
    this.listError = config.listError ?? { type: 'none' };
    this.mutationError = config.mutationError ?? { type: 'none' };
  }

  /** Runtime reconfiguration, for the Playwright bridge. */
  configure(config: MockExternalAccessApiClientConfig): void {
    if (config.items !== undefined) this.items = config.items;
    if (config.truncated !== undefined) this.truncated = config.truncated;
    if (config.canManage !== undefined) this.canManage = config.canManage;
    if (config.listError !== undefined) this.listError = config.listError;
    if (config.mutationError !== undefined)
      this.mutationError = config.mutationError;
  }

  async listExternalAccess(_orgId: string): Promise<ExternalAccessResponse> {
    const error = toError(this.listError);
    if (error) throw error;
    return {
      items: [...this.items],
      truncated: this.truncated,
      canManage: this.canManage,
    };
  }

  async grantExternalAccess(
    _orgId: string,
    userId: string,
    role: UserRole,
  ): Promise<ExternalAccessMutationResponse> {
    const error = toError(this.mutationError);
    if (error) throw error;
    // The mock keeps the same contract the real service does: the row that
    // appears afterwards comes from the list, so record it here.
    this.items = [
      ...this.items,
      {
        userId,
        displayName: `Account ${userId}`,
        loginName: `${userId}@another.example`,
        role,
        homeOrgId: 'org-home',
        grantedAt: new Date().toISOString(),
      },
    ];
    return { success: true, message: 'External access granted' };
  }

  async revokeExternalAccess(
    _orgId: string,
    userId: string,
  ): Promise<ExternalAccessMutationResponse> {
    const error = toError(this.mutationError);
    if (error) throw error;
    this.items = this.items.filter((item) => item.userId !== userId);
    return { success: true, message: 'External access revoked' };
  }

  async setExternalAccessRole(
    _orgId: string,
    userId: string,
    role: UserRole,
  ): Promise<ExternalAccessMutationResponse> {
    const error = toError(this.mutationError);
    if (error) throw error;
    this.items = this.items.map((item) =>
      item.userId === userId ? { ...item, role } : item,
    );
    return { success: true, message: `Role changed to ${role}` };
  }
}
